import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { fetchQueueStatus } from '@sophocles/api/queueStatus';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import { parseApiError } from '@sophocles/utils/apiErrors';

const REFRESH_OPTIONS = [
  { label: '5s', value: 5000 },
  { label: '10s', value: 10000 },
  { label: '30s', value: 30000 },
];

/**
 * Build a nested tree from flat dot-separated keys.
 *
 * Input:  { "message.in.v1.short-status": 42, "message.in.v1.long-status": 18, "celery": 5 }
 * Output: { message: { children: { in: { children: { v1: { children: {
 *            "short-status": { value: 42 }, "long-status": { value: 18 } } } } } } },
 *           celery: { value: 5 } }
 */
function buildTree(flatData) {
  const tree = {};

  Object.keys(flatData)
    .sort()
    .forEach(key => {
      const parts = key.split('.');
      let node = tree;

      parts.forEach((part, i) => {
        if (i === parts.length - 1) {
          // Leaf node — if it already exists as a branch, attach value to it
          if (node[part] && node[part].children) {
            node[part].value = flatData[key];
          } else {
            node[part] = { ...(node[part] || {}), value: flatData[key] };
          }
        } else {
          // Branch node
          if (!node[part]) {
            node[part] = { children: {} };
          } else if (!node[part].children) {
            node[part].children = {};
          }
          node = node[part].children;
        }
      });
    });

  return tree;
}

/**
 * Recursively render tree nodes as indented table rows.
 */
function TreeRows({ tree, previousData, depth, collapsed, onToggle, pathPrefix }) {
  // Sort: branches (nodes with children) first, then leaves, alphabetically within each group
  const sortedKeys = Object.keys(tree).sort((a, b) => {
    const aHasChildren = tree[a].children && Object.keys(tree[a].children).length > 0;
    const bHasChildren = tree[b].children && Object.keys(tree[b].children).length > 0;
    if (aHasChildren && !bHasChildren) return -1;
    if (!aHasChildren && bHasChildren) return 1;
    return a.localeCompare(b);
  });
  const rows = [];

  sortedKeys.forEach(key => {
    const node = tree[key];
    const fullPath = pathPrefix ? `${pathPrefix}.${key}` : key;
    const hasChildren = node.children && Object.keys(node.children).length > 0;
    const hasValue = node.value !== undefined;
    const isCollapsed = collapsed[fullPath] || false;

    rows.push(
      <TableRow key={fullPath} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell
          component="th"
          scope="row"
          sx={{ pl: 2 + depth * 3, py: hasChildren ? 0.5 : undefined, whiteSpace: 'nowrap' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasChildren ? (
              <IconButton size="small" onClick={() => onToggle(fullPath)} sx={{ p: 0 }}>
                {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: hasChildren ? 600 : 400,
                color: hasChildren && !hasValue ? 'text.secondary' : 'text.primary',
              }}
            >
              {key}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
          {hasValue ? node.value : ''}
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {hasValue ? (previousData?.[fullPath] ?? '-') : ''}
        </TableCell>
      </TableRow>,
    );

    if (hasChildren && !isCollapsed) {
      rows.push(
        ...TreeRows({
          tree: node.children,
          previousData,
          depth: depth + 1,
          collapsed,
          onToggle,
          pathPrefix: fullPath,
        }),
      );
    }
  });

  return rows;
}

function QueueStatusPage(props) {
  const [loading, setLoading] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const intervalRef = useRef(null);
  const currentDataRef = useRef(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetchQueueStatus()
      .then(newData => {
        setPreviousData(currentDataRef.current);
        setCurrentData(newData);
        currentDataRef.current = newData;
        setError(null);
      })
      .catch(err => {
        // On a broker outage the counts stop moving; without this the page
        // would keep showing the last good numbers as if they were current.
        const { nonFieldMessages } = parseApiError(err);
        setError(nonFieldMessages.join(' '));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshInterval) {
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refreshInterval, fetchData]);

  const handleToggle = path => {
    setCollapsed(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const handleRefreshIntervalChange = (_event, newValue) => {
    setRefreshInterval(newValue);
  };

  const sortedCategories = currentData ? Object.keys(currentData).sort() : [];

  return (
    <AppNav {...props} loading={loading} page="admin_home" notFullHeight>
      <Box sx={{ top: '64px', mx: 2, pb: 2, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
        <TitleBar
          showBackButton
          title="Queue Status Report"
          items={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ToggleButtonGroup value={refreshInterval} exclusive onChange={handleRefreshIntervalChange} size="small">
                {REFRESH_OPTIONS.map(opt => (
                  <ToggleButton key={opt.value} value={opt.value} sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchData}>{loading ? <CircularProgress size={20} /> : <SyncIcon />}</IconButton>
              </Tooltip>
            </Box>
          }
        />
        {error && (
          <Alert severity="error" sx={{ my: 1 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ flex: 1, overflow: 'auto', alignContent: 'flex-start' }}>
          {sortedCategories.map(category => {
            const categoryData = currentData[category];
            const prevCategoryData = previousData?.[category] || null;
            const tree = buildTree(categoryData);
            const fieldCount = Object.keys(categoryData).length;
            const isQueueCategory = category.toLowerCase().includes('queue');
            const itemLabel = isQueueCategory ? 'queues' : 'items';
            const columnLabel = isQueueCategory ? 'Queue' : 'Field';

            return (
              <Grid key={category} size={{ lg: 6, md: 6, xs: 12 }}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                        {category}
                      </Typography>
                      <Chip label={`${fieldCount} ${itemLabel}`} size="small" variant="outlined" />
                    </Box>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>{columnLabel}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>
                              Current
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>
                              Previous
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {TreeRows({
                            tree,
                            previousData: prevCategoryData,
                            depth: 0,
                            collapsed,
                            onToggle: handleToggle,
                            pathPrefix: '',
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </AppNav>
  );
}

export default QueueStatusPage;
